import React, { useState } from "react";
import useLocalStorage from "hooks/useLocalStorage";
import { notification, Table } from "antd";
import { useLocation } from "react-router-dom";

import {
  useQueryParams,
  NumberParam,
  withDefault,
  StringParam,
} from "use-query-params";
import { useQuery } from "@tanstack/react-query";
import { api, createQuery } from "helpers/api";

import Filters from "./components/Filters";
import { useAuth } from "context/providers/auth";

const MainList = ({
  columns,
  query_name = "",
  query_path = "",
  query_context,
  default_sort = "",
  navigation_path,
  selectable,
  ls_key,
  show_checklist = false,
  checked_list,
  updateCheckedList,
  default_ls_query = {},
  default_ls_search_values = {},
  col_xl = 4,
  col_lg = 6,
  col_md = 12,
  col_xs = 24,
}) => {
  const {
    state: {
      user: { company },
    },
  } = useAuth();

  const { pathname } = useLocation();
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [paginate, setPaginate] = useQueryParams({
    page: withDefault(NumberParam, 1),
    per_page: withDefault(NumberParam, 15),
    sort: withDefault(StringParam, default_sort),
  });

  const [query, setQuery] = useState({});
  const [search_values, setSearchValues] = useState({});

  const [ls_query, setLsQuery] = useLocalStorage(
    ls_key ? `${company?.id}_${ls_key}_query` : null,
    default_ls_query
  );

  const [ls_search_values, setLsSearchValues] = useLocalStorage(
    ls_key ? `${company?.id}_${ls_key}_search_values` : null,
    default_ls_search_values
  );

  const main_list_query = ls_key ? ls_query : query;
  const main_list_search_values = ls_key ? ls_search_values : search_values;

  const setMainListQuery = ls_key ? setLsQuery : setQuery;
  const setMainListSearchValues = ls_key ? setLsSearchValues : setSearchValues;

  const { isLoading: is_loading, data } = useQuery(
    [query_name, paginate, main_list_query],
    () =>
      api.get(
        `${query_path}?${createQuery({
          context: query_context,
          paginate,
          query: main_list_query,
        })}`
      ),
    {
      retry: false,
      keepPreviousData: true,
      onError: (err) =>
        notification.error({
          message: err?.response?.data?.message || "Wystąpił błąd",
        }),
    }
  );

  const onTableDataChange = (pagination, _, sort, { action }) => {
    if (action === "paginate") {
      setPaginate({
        ...paginate,
        page: pagination.current,
        per_page: pagination.pageSize,
      });
      return;
    }
  };

  const onSearch = ({ name, value, search_method }) =>
    setMainListQuery({
      ...main_list_query,
      [name]: { value, search_method },
    });

  const onSelectChange = (newSelectedRowKeys) =>
    setSelectedRowKeys(newSelectedRowKeys);

  const rowSelection = {
    selectedRowKeys,
    onChange: onSelectChange,
    selections: null,
  };

  const table_columns = show_checklist
    ? columns.filter((item) => checked_list.includes(item.dataIndex))
    : columns;

  const handleSetSearchValues = (values) => {
    setMainListSearchValues(values);
    if (pathname === "/issues") {
      if (
        values?.worker_billing_status === 0 ||
        values?.administration_billing_status === 0
      ) {
        setPaginate({
          ...paginate,
          page: undefined,
          sort: "service_date__asc",
        });
        return;
      }
      if ([undefined, 2].includes(values?.status)) {
        setPaginate({
          ...paginate,
          page: undefined,
          sort: "service_date__desc",
        });
        return;
      }
      if ([0, 1].includes(values?.status)) {
        setPaginate({
          ...paginate,
          page: undefined,
          sort: "service_date__asc",
        });
        return;
      }
    }
    setPaginate({
      ...paginate,
      page: undefined,
    });
  };

  return (
    <>
      <Filters
        query={main_list_query}
        columns={columns}
        search_values={main_list_search_values}
        onSearch={onSearch}
        setSearchValues={handleSetSearchValues}
        setQuery={setMainListQuery}
        paginate={paginate}
        setPaginate={setPaginate}
        total_count={data?.meta?.total_count}
        show_checklist={show_checklist}
        checked_list={checked_list}
        updateCheckedList={updateCheckedList}
        {...{ col_xl, col_lg, col_md, col_xs }}
      />
      <Table
        navigation_path={navigation_path}
        loading={is_loading}
        rowSelection={selectable ? rowSelection : null}
        columns={table_columns}
        scroll={{ x: "100%" }}
        pagination={{
          current: data?.meta?.current_page || 1,
          pageSize: data?.meta?.per_page || 10,
          total: data?.meta?.total_count || 0,
          pageSizeOptions: ["10", "15", "30", "50"],
        }}
        dataSource={data?.data?.map((item) => ({
          ...item,
          key: item.id,
        }))}
        onChange={onTableDataChange}
      />
    </>
  );
};

export default MainList;
